import React, { useState, ReactNode } from 'react';
import { SearchContext } from './search-context';
import { Doctor, BlogPost, Topic, Specialty, supabase } from '../lib/supabase';

interface SearchResults {
  doctors: Doctor[];
  blogPosts: BlogPost[];
  topics: Topic[];
  specialties: Specialty[];
}

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults>({
    doctors: [],
    blogPosts: [],
    topics: [],
    specialties: []
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const performSearch = async (term: string) => {
    if (!term.trim()) {
      setSearchResults({
        doctors: [],
        blogPosts: [],
        topics: [],
        specialties: []
      });
      return;
    }

    setLoading(true);
    setError(null);
    setSearchTerm(term);
    
    try {
      // Normalize search term for better matching
      const normalizedTerm = term.toLowerCase()
        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
        .replace(/[^a-z0-9\s]/g, ' ') // Replace special chars with spaces
        .replace(/\s+/g, ' ') // Normalize multiple spaces
        .trim();
      
      // Create search patterns for different variations
      const searchPatterns = [
        term, // Original term
        normalizedTerm, // Normalized term
        term.replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss'), // Just umlauts
        ...normalizedTerm.split(' ').filter(word => word.length > 2) // Individual words
      ];
      
      // Search doctors. Hard-cap auf 200 Treffer — eine Suchergebnis-Seite
      // braucht keine 11k Rows, und ohne expliziten Limit greift der
      // PostgREST-Default von 1000.
      const { data: doctorResults, error: doctorError } = await supabase
        .from('doctors')
        .select('*')
        .eq('status', 'active')
        .order('city', { ascending: true })
        .or(searchPatterns.map(pattern =>
          `first_name.ilike.%${pattern}%,last_name.ilike.%${pattern}%,provider_name.ilike.%${pattern}%,city.ilike.%${pattern}%,services.ilike.%${pattern}%,bio.ilike.%${pattern}%,email.ilike.%${pattern}%,phone.ilike.%${pattern}%`
        ).join(','))
        .limit(200);

      // Search doctors by their linked topics. Auch hier explizit limitiert,
      // damit ein Topic mit >1000 verlinkten Doctors nicht ins PostgREST-Cap läuft.
      const { data: partialTopicDoctorResults, error: partialTopicDoctorError } = await supabase
        .from('doctor_topics')
        .select(`
          doctor:doctors!inner(*)
        `)
        .eq('doctors.status', 'active')
        .in('topic_id',
          (await supabase
            .from('topics')
            .select('id')
            .or(searchPatterns.map(pattern => `name.ilike.%${pattern}%`).join(','))
            .eq('is_active', true)
          ).data?.map(t => t.id) || []
        )
        .limit(200);

      if (doctorError) throw doctorError;
      if (partialTopicDoctorError) throw partialTopicDoctorError;

      // Combine and deduplicate results
      const topicDoctors = (partialTopicDoctorResults || []).map(item => item.doctor).filter(Boolean);
      const allDoctors = [
        ...(doctorResults || []), 
        ...topicDoctors
      ];
      const uniqueDoctors = allDoctors.filter((doctor, index, self) => 
        index === self.findIndex(d => d.id === doctor.id)
      );

      // Sort results: Premium first, then by creation date
      const sortedDoctors = uniqueDoctors.sort((a, b) => {
        // First priority: Premium plan only
        const aIsPremium = a.plan === 'premium';
        const bIsPremium = b.plan === 'premium';
        
        if (aIsPremium !== bIsPremium) {
          return bIsPremium ? 1 : -1;
        }
        
        // Second priority: Creation date (newest first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      // Search blog posts
      const { data: blogResults, error: blogError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .or(searchPatterns.map(pattern => 
          `title.ilike.%${pattern}%,excerpt.ilike.%${pattern}%,content.ilike.%${pattern}%,author.ilike.%${pattern}%,category.ilike.%${pattern}%`
        ).join(','))
        .order('published_at', { ascending: false });

      // Search topics (diseases, symptoms, consultations)
      const { data: topicResults, error: topicError } = await supabase
        .from('topics')
        .select('*')
        .or(searchPatterns.map(pattern => 
          `name.ilike.%${pattern}%,description.ilike.%${pattern}%,content.ilike.%${pattern}%`
        ).join(','))
        .order('name', { ascending: true });

      // Search specialties
      const { data: specialtyResults, error: specialtyError } = await supabase
        .from('specialties')
        .select('*')
        .eq('is_active', true)
        .or(searchPatterns.map(pattern => 
          `name.ilike.%${pattern}%,description.ilike.%${pattern}%`
        ).join(','))
        .order('name', { ascending: true });

      if (doctorError) throw doctorError;
      if (blogError) throw blogError;
      if (topicError) throw topicError;
      if (specialtyError) throw specialtyError;

      setSearchResults({
        doctors: sortedDoctors,
        blogPosts: blogResults || [],
        topics: topicResults || [],
        specialties: specialtyResults || []
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler bei der Suche');
      setSearchResults({
        doctors: [],
        blogPosts: [],
        topics: [],
        specialties: []
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SearchContext.Provider value={{ searchTerm, searchResults, loading, error, performSearch }}>
      {children}
    </SearchContext.Provider>
  );
};